import { Component, DestroyRef, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser, NgOptimizedImage } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { BusinessService, LocationService } from 'briteatlib';
import { SharedModule } from '../../../modules/shared/shared.module';
import { OrderService } from '../../../services/order.service';

type HomeCategory = { id?: string; title?: string; image?: string; url?: string; sortOrder?: number };
type ServiceFeature = { title?: string; caption?: string; image?: string; icon?: string };

@Component({
  selector: 'brt-briteat-home',
  standalone: true,
  imports: [SharedModule, NgOptimizedImage],
  templateUrl: './briteat-home.component.html',
  styleUrl: './briteat-home.component.scss'
})
export class BriteatHomeComponent {
  ui: { [key: string]: string } = {};
  categories: HomeCategory[] = [];
  serviceFeatures: ServiceFeature[] = [];
  popular: any[] = [];
  postcode = '';
  loadingPopular = true;
  detectingPostcode = false;
  locationMessage = '';

  // Hero slideshow (up to 10 photos, auto-rotating + swipeable).
  heroPhotos: string[] = [];
  heroSlide = 0;
  heroDragging = false;
  // Indices whose <img> has actually been rendered. We materialise the current slide and
  // its neighbours eagerly so autoplay/search interactions can never reveal an empty card,
  // while still avoiding the cost of downloading all 10 images on first paint.
  private loadedSlides = new Set<number>([0]);
  private heroDragOffset = 0; // current drag distance as a percentage of the viewport width
  private heroDragStartX = 0;
  private heroViewportWidth = 1;
  private heroTimer: any = null;

  constructor(
    private businessService: BusinessService,
    private router: Router,
    private orderService: OrderService,
    private locationService: LocationService,
    private destroyRef: DestroyRef,
    @Inject(PLATFORM_ID) private platformId: Object
  ) { }

  async ngOnInit() {
    // Single request for everything the initial render needs (UI config + popular
    // businesses), replacing the previous system-config -> published -> published(fallback)
    // waterfall. Falls back to the legacy two-step path for older backends.
    try {
      const home = await this.businessService.getHome();
      this.applyUi(home?.UI || {});
      this.popular = home?.PopularBusinesses || [];
      this.loadingPopular = false;
    } catch {
      await this.loadHomeLegacy();
    }

    this.orderService.currentPostcode$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(postcode => this.postcode = postcode || this.postcode);
  }

  ngOnDestroy() {
    this.stopHeroAutoplay();
  }

  private applyUi(ui: { [key: string]: string }) {
    this.ui = ui || {};
    this.categories = this.parseJson<HomeCategory[]>(this.ui['HomeCategories'], [])
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
    this.serviceFeatures = this.parseJson<ServiceFeature[]>(this.ui['HomeServiceFeatures'], []);
    this.setupHeroPhotos();
  }

  // Backwards-compatible path used when the /api/business/home endpoint is unavailable.
  private async loadHomeLegacy() {
    try {
      const config = await this.businessService.getSystemConfig();
      this.applyUi(config?.UI || {});
    } catch {
      this.applyUi({});
    }
    await this.loadPopular();
  }

  // ===================== HERO SLIDESHOW =====================
  // Photos come from the HomeHeroPhotos JSON array (strings or { image/url } objects),
  // falling back to the single HomeHeroPhoto. Capped at 10 slides.
  private setupHeroPhotos() {
    const raw = this.parseJson<any[]>(this.ui['HomeHeroPhotos'], []);
    const list = (Array.isArray(raw) ? raw : [])
      .map(item => (typeof item === 'string' ? item : item?.image || item?.url || ''))
      .filter(Boolean);
    if (!list.length) {
      const single = this.cfg('HomeHeroPhoto');
      if (single) list.push(single);
    }
    this.heroPhotos = list.slice(0, 10);
    this.heroSlide = 0;
    this.loadedSlides = new Set<number>([0]);
    this.markNeighboursLoaded(0);
    this.startHeroAutoplay();
  }

  get heroTrackTransform(): string {
    return `translateX(calc(${-this.heroSlide * 100}% + ${this.heroDragOffset}%))`;
  }

  // A slide's <img> is only rendered once it is the current slide, an immediate neighbour,
  // or has been visited before (kept so re-visits don't re-request). Rendered neighbours
  // are intentionally eager: native lazy loading may otherwise defer translated, off-screen
  // slides until after the carousel has moved, briefly making the hero image disappear.
  isSlideLoaded(index: number): boolean {
    return this.loadedSlides.has(index);
  }

  private markNeighboursLoaded(index: number) {
    const count = this.heroPhotos.length;
    if (!count) return;
    this.loadedSlides.add(index);
    this.loadedSlides.add(((index + 1) % count + count) % count);
    this.loadedSlides.add(((index - 1) % count + count) % count);
  }

  goToHeroSlide(index: number) {
    const count = this.heroPhotos.length;
    if (!count) return;
    this.heroSlide = ((index % count) + count) % count;
    this.markNeighboursLoaded(this.heroSlide);
  }

  startHeroAutoplay() {
    this.stopHeroAutoplay();
    if (!isPlatformBrowser(this.platformId) || this.heroPhotos.length < 2) return;
    const seconds = parseFloat(this.cfg('HomeHeroSlideshowIntervalSeconds', '5')) || 5;
    this.heroTimer = setInterval(() => this.goToHeroSlide(this.heroSlide + 1), seconds * 1000);
  }

  stopHeroAutoplay() {
    if (this.heroTimer) {
      clearInterval(this.heroTimer);
      this.heroTimer = null;
    }
  }

  onHeroPointerDown(event: PointerEvent) {
    if (this.heroPhotos.length < 2) return;
    this.heroDragging = true;
    this.heroDragStartX = event.clientX;
    this.heroViewportWidth = (event.currentTarget as HTMLElement).offsetWidth || 1;
    (event.currentTarget as HTMLElement).setPointerCapture?.(event.pointerId);
    this.stopHeroAutoplay();
  }

  onHeroPointerMove(event: PointerEvent) {
    if (!this.heroDragging) return;
    this.heroDragOffset = ((event.clientX - this.heroDragStartX) / this.heroViewportWidth) * 100;
  }

  onHeroPointerUp() {
    if (!this.heroDragging) return;
    this.heroDragging = false;
    const threshold = 18; // percentage of the viewport before we advance a slide
    if (this.heroDragOffset <= -threshold) this.goToHeroSlide(this.heroSlide + 1);
    else if (this.heroDragOffset >= threshold) this.goToHeroSlide(this.heroSlide - 1);
    this.heroDragOffset = 0;
    this.startHeroAutoplay();
  }

  // The four benefit blocks shown under the hero search.
  // A JSON override (HomeServiceFeatures) wins, otherwise the individual keys are used.
  get benefitBlocks(): ServiceFeature[] {
    if (this.serviceFeatures.length)
      return this.serviceFeatures;
    return [
      { title: this.cfg('HomeFeatureFastDeliveryTitle', 'Fast delivery'), caption: this.cfg('HomeFeatureFastDeliveryCaption'), image: this.cfg('HomeFeatureFastDeliveryImage'), icon: 'pi-truck' },
      { title: this.cfg('HomeFeatureTopRestaurantsTitle', 'Top restaurants'), caption: this.cfg('HomeFeatureTopRestaurantsCaption'), image: this.cfg('HomeFeatureTopRestaurantsImage'), icon: 'pi-star' },
      { title: this.cfg('HomeFeatureGreatOffersTitle', 'Great offers'), caption: this.cfg('HomeFeatureGreatOffersCaption'), image: this.cfg('HomeFeatureGreatOffersImage'), icon: 'pi-tag' },
      { title: this.cfg('HomeFeatureSecurePaymentsTitle', 'Secure payments'), caption: this.cfg('HomeFeatureSecurePaymentsCaption'), image: this.cfg('HomeFeatureSecurePaymentsImage'), icon: 'pi-lock' },
    ].filter(b => b.title);
  }

  // The footer story tiles above the app banner.
  get storyTiles(): ServiceFeature[] {
    return [
      { title: this.cfg('FooterLiveOrderTrackingTitle', 'Live order tracking'), caption: this.cfg('FooterLiveOrderTrackingCaption'), image: this.cfg('FooterLiveOrderTrackingImage'), icon: 'pi-map-marker' },
      { title: this.cfg('FooterExclusiveOffersTitle', 'Exclusive offers'), caption: this.cfg('FooterExclusiveOffersCaption'), image: this.cfg('FooterExclusiveOffersImage'), icon: 'pi-heart' },
      { title: this.cfg('FooterMultiplePaymentsTitle', 'Multiple payment options'), caption: this.cfg('FooterMultiplePaymentsCaption'), image: this.cfg('FooterMultiplePaymentsImage'), icon: 'pi-credit-card' },
      { title: this.cfg('FooterSupportTitle', 'Support 7 days a week'), caption: this.cfg('FooterSupportCaption'), image: this.cfg('FooterSupportImage'), icon: 'pi-comments' },
    ].filter(s => s.title);
  }

  // Read a UI setting with an inline fallback (server already fills key defaults).
  cfg(key: string, fallback = ''): string {
    const value = this.ui?.[key];
    return value === undefined || value === null || value === '' ? fallback : value;
  }

  private parseJson<T>(raw: string | undefined, fallback: T): T {
    if (!raw) return fallback;
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) || typeof parsed === 'object' ? parsed : fallback;
    } catch {
      return fallback;
    }
  }

  private async loadPopular() {
    this.loadingPopular = true;
    try {
      const max = parseInt(this.cfg('HomePopularNearYouMaxItems', '10'), 10) || 10;
      const criteria: any = { IsPopularNearYou: true, Size: max };
      const postcode = this.cfg('HomePopularNearYouPostcode') || this.postcode;
      if (postcode) criteria.NearTo = postcode;

      let result = await this.businessService.getAll(criteria);
      let list = result?.List || [];

      // If no business is flagged as Popular Near You, fall back to the latest published ones
      // so the section is never empty on the landing page.
      if (!list.length) {
        const fallback: any = { Size: max };
        if (postcode) fallback.NearTo = postcode;
        result = await this.businessService.getAll(fallback);
        list = result?.List || [];
      }
      this.popular = list;
    } catch {
      this.popular = [];
    } finally {
      this.loadingPopular = false;
    }
  }

  signup() {
    this.router.navigate(['/login'], { queryParams: { page: 'signup' } });
  }

  findFood() {
    const postcode = this.postcode?.trim();
    if (postcode && postcode !== 'Out of UK')
      this.orderService.currentPostcode$.next(postcode);
    this.router.navigate(['/restaurants'], { queryParams: postcode && postcode !== 'Out of UK' ? { postcode } : undefined });
  }

  async detectPostcodeFromMyLocation() {
    this.locationMessage = '';
    if (!navigator.geolocation) {
      this.locationMessage = 'Geolocation is not supported in this browser.';
      return;
    }

    this.detectingPostcode = true;
    try {
      const position = await new Promise<GeolocationPosition>((resolve, reject) =>
        navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true, timeout: 15000 }));
      const postcode = await this.locationService.detectUkPostcodeFromCoordinates(
        position.coords.latitude,
        position.coords.longitude
      );
      this.postcode = postcode;
      this.orderService.currentPostcode$.next(postcode);
      this.locationMessage = `Current post code: ${postcode}`;
    } catch (error: any) {
      this.postcode = '';
      this.locationMessage = this.isOutOfUkError(error) ? 'Out of UK' : 'Unable to detect postcode from your location.';
    } finally {
      this.detectingPostcode = false;
    }
  }

  private isOutOfUkError(error: any): boolean {
    const value = error?.error?.error || error?.message || error;
    return typeof value === 'string' && value.toLowerCase() === 'out of uk';
  }

  navigate(url?: string) {
    if (!url) return;
    if (/^https?:\/\//i.test(url)) {
      window.open(url, '_blank');
      return;
    }
    const [path, query] = url.split('?');
    const queryParams: any = {};
    if (query)
      for (const part of query.split('&')) {
        const [k, v] = part.split('=');
        if (k) queryParams[decodeURIComponent(k)] = decodeURIComponent(v || '');
      }
    this.router.navigate([path], { queryParams });
  }


  restaurantImage(business: any): string {
    return business?.UI?.['BackgroundImage'] || business?.UI?.Image || business?.UI?.Logo || this.cfg('HomeHeroPhoto');
  }

  etaLabel(business: any): string {
    const deliveryEta = this.findEtaRange(business, 'Delivery') || this.findEtaRange(business, 'Default') || this.findEtaRange(business);
    if (!deliveryEta) return '';

    const lower = deliveryEta.rangeLower ?? deliveryEta.RangeLower ?? deliveryEta.lower ?? deliveryEta.Lower;
    const upper = deliveryEta.rangeUpper ?? deliveryEta.RangeUpper ?? deliveryEta.upper ?? deliveryEta.Upper;
    const minutes = deliveryEta.Minutes ?? deliveryEta.minutes;

    if (lower && upper) return `${lower}-${upper} min`;
    if (minutes) return `${minutes} min`;
    return '';
  }

  ratingLabel(business: any): string {
    const rating = business?.Rating ?? business?.rating?.starRating ?? business?.UI?.Rating;
    const parsed = typeof rating === 'string' ? parseFloat(rating) : rating;
    return Number.isFinite(parsed) && parsed > 0 ? parsed.toFixed(1) : '';
  }

  cuisineLabel(business: any): string {
    const cuisines = Array.isArray(business?.CuisineTypes) ? business.CuisineTypes.filter(Boolean) : [];
    if (cuisines.length) return cuisines.slice(0, 2).join(' · ');
    return business?.UI?.Slogan || business?.UI?.HighlightFeatures || 'Restaurant';
  }

  deliveryLabel(business: any): string {
    const minOrder = this.moneyLabel(business?.MinDeliveryOrderAmount || business?.DeliveryZones?.[0]?.MinOrderAmount);
    const deliveryFee = this.deliveryFeeLabel(business?.DeliveryZones?.[0]?.DeliveryFee);
    if (minOrder && deliveryFee) return `${deliveryFee} · Min order ${minOrder}`;
    if (minOrder) return `Min order ${minOrder}`;
    if (deliveryFee) return deliveryFee;
    return business?.UI?.HighlightFeatures || 'Order online';
  }

  private findEtaRange(business: any, orderType?: string): any {
    const etaRanges = business?.DefaultEtaMinutes || business?.deliveryEtaMinutes;
    if (!Array.isArray(etaRanges)) return etaRanges;
    return etaRanges.find((eta: any) => !orderType || eta.OrderType === orderType || eta.orderType === orderType);
  }

  private moneyLabel(value: any): string {
    if (value === undefined || value === null || value === '') return '';
    const parsed = typeof value === 'string' ? parseFloat(value) : value;
    return Number.isFinite(parsed) && parsed > 0 ? `£${parsed}` : '';
  }

  private deliveryFeeLabel(value: any): string {
    if (value === undefined || value === null || value === '') return '';
    const parsed = typeof value === 'string' ? parseFloat(value) : value;
    if (!Number.isFinite(parsed)) return '';
    return parsed > 0 ? `Delivery £${parsed}` : 'Free delivery';
  }

  businessUrl(business: any): string {
    const domain = business?.Domains?.[0];
    return domain ? `https://${domain}` : '#';
  }

  initial(title?: string): string {
    return (title || '?').trim().charAt(0).toUpperCase();
  }
}
